import { useState, useEffect, Fragment } from "react";
import { formatDisplayDate, calculatePitchDueDate } from "../../utils/dates";
import { Link } from "react-router-dom";
import { pitchesApi } from "../../../repositories/pitches";
import Swal from "sweetalert2";
import { Menu, Transition } from "@headlessui/react";
import {
  EllipsisVerticalIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";
import DSPDeliveryModal from "../../../components/modal/DSPDeliveryModal";
import { PencilIcon } from "lucide-react";

interface DashboardTableRowProps {
  data: {
    id: number;
    userId: number;
    companyName: string;
    artistName: string;
    releaseTitle: string;
    releaseDate: string;
    configuration: string;
    releaseUPC: string;
    trackISRC: string;
    status: string;
    deliveredDSPs?: string[];
    AmazonInfo?: any;
    SpotifyInfo?: any;
    DeezerInfo?: any;
    TidalInfo?: any;
    AudiomackInfo?: any;
  };
  onStatusChange: () => void;
  checked?: boolean; // <-- Add this prop
  onCheck?: (id: number, checked: boolean) => void; // <-- Add this prop
}

export default function DashboardTableRow({
  data,
  onStatusChange,
  checked = false ,
  onCheck,
}: DashboardTableRowProps) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [status, setStatus] = useState(data.status || "submitted");
  const [modalMode, setModalMode] = useState<"admin" | "user">("admin");

  useEffect(() => {
    try {
      const authData = localStorage.getItem("auth");
      if (authData) {
        const parsedData = JSON.parse(authData);
        setIsAdmin(parsedData.user?.role === "admin");
      }
    } catch (error) {
      console.error("Error reading localStorage:", error);
    }
  }, []);

  useEffect(() => {
    setStatus(data.status || "submitted");
  }, [data.status]);

  const handleMarkAsDelivered = () => {
    setModalMode("admin");
    setIsModalOpen(true);
  };
  const handleUpdateDSPs = () => {
    setModalMode("admin");
    setIsModalOpen(true);
  };
  const handleUserDeliveredClick = () => {
    if (data.status === "delivered" && !isAdmin) {
      setModalMode("user");
      setIsModalOpen(true);
    }
  };

  const handleDSPSubmit = async (selectedDSPs: string[]) => {
    try {
      await pitchesApi.updateDelivery(data.id, {
        status: "delivered",
        deliveredDSPs: selectedDSPs,
      });
      setStatus("delivered");
      setIsModalOpen(false);
      onStatusChange();
      Swal.fire({
        title: "Success!",
        text: "Pitch delivered successfully!",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error: any) {
      Swal.fire({
        title: "Error!",
        text: error?.response?.data?.message || "Failed to update delivery",
        icon: "error",
      });
    }
  };

  const getStatusBadge = (currentStatus: string) => {
    const baseClasses =
      "inline-flex w-24 items-center justify-center px-3 py-1 rounded-full text-sm font-medium";

    switch (currentStatus) {
      case "submitted":
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            Submitted
          </span>
        );
      case "delivered":
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            Delivered
          </span>
        );
      case "draft":
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            Draft
          </span>
        );
      default:
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {currentStatus}
          </span>
        );
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onCheck) {
      onCheck(data.id, e.target.checked);
    }
  };

  return (
    <>
      <tr className="transition border-b hover:bg-gray-100">
        {isAdmin && ( 
          <td className="p-4">
          <input
            type="checkbox"
            checked={checked}
            onChange={handleCheckboxChange}
            aria-label={`Select pitch ${data.artistName} - ${data.releaseTitle}`}
            className="form-checkbox h-5 w-5 text-blue-600"
          />
        </td>
        )}
        {isAdmin && <td className="p-4">{data.companyName}</td>}
        <td className="p-4">{data.artistName}</td>
        <td className="p-4">{data.releaseTitle}</td>
        <td className="p-4">{formatDisplayDate(data.releaseDate)}</td>
        <td className="p-4">{calculatePitchDueDate(data.releaseDate)}</td>

        <td className="p-4">
          {data.status === "draft" || data.status==="Needs Pitch" ? (
            <Link
              className="inline-block w-20 px-4 py-2 font-medium text-center text-white transition-colors duration-200 bg-orange-800 rounded-md hover:bg-brown-500"
              to={`/pitch/edit/${data.id}`}
            >
              Edit
            </Link>
          ) : (
            <Link
              className="inline-block w-20 px-4 py-2 font-medium text-center text-white transition-colors duration-200 bg-yellow-400 rounded-md hover:bg-yellow-500"
              to={`/pitch/submission/${data.id}`}
            >
              Review
            </Link>
          )}
        </td>
        <td className="p-4">
          {isAdmin ? (
            <div className="flex items-center justify-between w-full">
              {/* Status Badge */}
              <div className="flex-shrink-0">{getStatusBadge(status)}</div>

              {/* Admin Actions Menu - Always show for admin */}
              {status !== "draft" && (
                <Menu as="div" className="relative inline-block text-left">
                  <Menu.Button className="p-2 rounded-md hover:bg-gray-200">
                    <EllipsisVerticalIcon className="w-5 h-5" />
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 w-48 text-lg bg-white divide-y divide-gray-100 rounded-md shadow-lg -top-5">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={
                              status === "delivered"
                                ? handleUpdateDSPs
                                : handleMarkAsDelivered
                            }
                            className={`${
                              active ? "bg-blue-100" : ""
                            } group flex w-full items-center px-4 py-2 text-sm text-gray-700 text-lg`}
                          >
                            {status === "delivered" ? (
                              <>
                                <PencilIcon className="w-10 h-8 mr-2 text-blue-500" />
                                Update DSPs
                              </>
                            ) : (
                              <>
                                <TruckIcon className="w-10 h-10 mr-2 text-green-500" />
                                Mark as Delivered
                              </>
                            )}
                          </button>
                        )}
                      </Menu.Item>
                    </Menu.Items>
                  </Transition>
                </Menu>
              )}
            </div>
          ) : (
            <div
              onClick={handleUserDeliveredClick}
              className={`${
                data.status === "delivered"
                  ? "cursor-pointer hover:bg-blue-50 rounded-md p-1 transition-colors"
                  : ""
              }`}
            >
              {data.status === "delivered" ? (
                <div className="flex items-center space-x-2 text-lg">
                  {getStatusBadge(data.status)}
                  <span className="text-xs text-blue-600 hover:text-blue-800">
                    View Details →
                  </span>
                </div>
              ) : (
                getStatusBadge(data.status)
              )}
            </div>
          )}
        </td>
      </tr>

      <DSPDeliveryModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleDSPSubmit}
        initialDSPs={data.deliveredDSPs || []}
        mode={modalMode}
        pitchId={data.id}
      />
    </>
  );
}
